/* eslint-disable react-hooks/exhaustive-deps */
/**
 * AGROSHOP MALI — Hooks React personnalisés
 * Gestion des données, du panier, de l'authentification
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { produitsService, commandesService, dashboardService, panierService } from '../services/api';


// ============================================================
// HOOK GÉNÉRIQUE — Chargement de données
// ============================================================

export function useData(fetchFn, deps = []) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState(null);

  const charger = useCallback(async () => {
    setLoading(true);
    setErreur(null);
    try {
      const resultat = await fetchFn();
      setData(resultat);
    } catch (e) {
      setErreur(e.message);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => { charger(); }, [charger]);

  return { data, loading, erreur, recharger: charger };
}


// ============================================================
// HOOK CATALOGUE — Produits avec filtres et recherche
// ============================================================

export function useCatalogue() {
  const [produits, setProduits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [filtres, setFiltres] = useState({});
  const [recherche, setRecherche] = useState('');
  const timerRef = useRef(null);

  const charger = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const data = await produitsService.getAll(params);
      setProduits(data.results || data);
    } catch (e) {
      setErreur(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounce sur la recherche (évite trop d'appels API)
  const chercherProduit = useCallback((terme) => {
    setRecherche(terme);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      charger({ search: terme, ...filtres });
    }, 400);
  }, [charger, filtres]);

  const appliquerFiltres = useCallback((nouveauxFiltres) => {
    setFiltres(nouveauxFiltres);
    charger({ search: recherche, ...nouveauxFiltres });
  }, [charger, recherche]);

  useEffect(() => { charger(); }, [charger]);

  return { produits, loading, erreur, recherche, chercherProduit, appliquerFiltres, recharger: charger };
}


// ============================================================
// HOOK PANIER
// ============================================================

export function usePanier(connected = false) {
  const [items, setItems] = useState(panierService.getItems());

  // Écoute les changements du panier depuis d'autres composants
  useEffect(() => {
    const handler = () => setItems(panierService.getItems());
    window.addEventListener('panier-mis-a-jour', handler);
    return () => window.removeEventListener('panier-mis-a-jour', handler);
  }, []);

  useEffect(() => {
    if (!connected) {
      return undefined;
    }

    const timer = setTimeout(async () => {
      try {
        await panierService.saveToServer();
      } catch {
        // Ne pas interrompre l'expérience utilisateur si la synchronisation échoue
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, [items, connected]);

  const ajouter = useCallback((produit, quantite = 1) => {
    const nouveauxItems = panierService.ajouter(produit, quantite);
    setItems(nouveauxItems);
  }, []);

  const modifier = useCallback((produitId, quantite) => {
    const nouveauxItems = panierService.modifier(produitId, quantite);
    setItems(nouveauxItems);
  }, []);

  const supprimer = useCallback((produitId) => {
    const nouveauxItems = panierService.supprimer(produitId);
    setItems(nouveauxItems);
  }, []);

  const vider = useCallback(() => {
    panierService.vider();
    setItems([]);
  }, []);

  const sousTotal = items.reduce((t, i) => t + i.produit.prix_unitaire * i.quantite, 0);
  const nbItems = items.reduce((t, i) => t + i.quantite, 0);

  return { items, nbItems, sousTotal, ajouter, modifier, supprimer, vider };
}


export function useWishlist() {
  const [items, setItems] = useState(wishlistService.getItems());

  useEffect(() => {
    const handler = () => setItems(wishlistService.getItems());
    window.addEventListener('wishlist-mis-a-jour', handler);
    return () => window.removeEventListener('wishlist-mis-a-jour', handler);
  }, []);

  const ajouter = useCallback((produit) => {
    const nouveauxItems = wishlistService.add(produit);
    setItems(nouveauxItems);
  }, []);

  const supprimer = useCallback((produitId) => {
    const nouveauxItems = wishlistService.remove(produitId);
    setItems(nouveauxItems);
  }, []);

  const toggle = useCallback((produit) => {
    const nouveauxItems = wishlistService.toggle(produit);
    setItems(nouveauxItems);
  }, []);

  const estEnregistre = useCallback((produitId) => wishlistService.isSaved(produitId), []);

  return { items, nbItems: items.length, ajouter, supprimer, toggle, estEnregistre };
}


// ============================================================
// HOOK PAIEMENT — avec polling de confirmation
// ============================================================

export function usePaiement() {
  const [etape, setEtape] = useState('formulaire'); // formulaire | attente | confirme | echec
  const [statutPaiement, setStatutPaiement] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [loading, setLoading] = useState(false);

  const initier = useCallback(async (commandeId, operateur, telephone) => {
    setLoading(true);
    setErreur(null);
    try {
      const { paiementsService } = await import('../services/api');
      const resultat = await paiementsService.initier(commandeId, operateur, telephone);

      // Si on a une URL de paiement (Orange Money, Wave) → ouvrir dans une nouvelle fenêtre
      if (resultat.url_paiement) {
        window.open(resultat.url_paiement, '_blank');
      }

      setEtape('attente');

      // Démarrer le polling pour attendre la confirmation
      await paiementsService.attendreConfirmation(
        resultat.paiement_id,
        (statut) => setStatutPaiement(statut)
      );

      setEtape('confirme');
      return resultat;

    } catch (e) {
      setErreur(e.message);
      setEtape('echec');
    } finally {
      setLoading(false);
    }
  }, []);

  const reinitialiser = useCallback(() => {
    setEtape('formulaire');
    setStatutPaiement(null);
    setErreur(null);
  }, []);

  return { etape, statutPaiement, erreur, loading, initier, reinitialiser };
}


// ============================================================
// HOOK DASHBOARD — Stats vendeur
// ============================================================

export function useDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [derniereMAJ, setDerniereMAJ] = useState(null);

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const data = await dashboardService.getStats();
      setStats(data);
      setDerniereMAJ(new Date());
    } catch (e) {
      setErreur(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    charger();
    // Auto-refresh toutes les 5 minutes
    const interval = setInterval(charger, 5 * 60 * 1000);

    const handleRefreshEvent = () => {
      charger();
    };
    window.addEventListener('agroshop-dashboard-refresh', handleRefreshEvent);

    return () => {
      clearInterval(interval);
      window.removeEventListener('agroshop-dashboard-refresh', handleRefreshEvent);
    };
  }, [charger]);

  return { stats, loading, erreur, derniereMAJ, recharger: charger };
}


// ============================================================
// HOOK FORMATAGE — Utilitaires d'affichage
// ============================================================

export function useFormatage() {
  const formatPrix = (montant) => {
    return `${parseInt(montant || 0).toLocaleString('fr-ML')} FCFA`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('fr-ML', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    });
  };

  const formatTelephone = (tel) => {
    if (!tel) return '';
    const t = tel.replace(/\D/g, '').replace(/^223/, '');
    if (t.length === 8) {
      return `+223 ${t.slice(0, 2)} ${t.slice(2, 4)} ${t.slice(4, 6)} ${t.slice(6)}`;
    }
    return tel;
  };

  return { formatPrix, formatDate, formatTelephone };
}
